/* oxlint-disable react/jsx-no-constructed-context-values */
import * as React from 'react';
import { Provider } from 'react-redux';
import { debounce } from '@cian/newbuilding-utils';
import { uaToDeviceType } from '@cian/ui-kit/utils';
import { UiKitRoot, UiKitToastsContextProvider } from '@cian/ui-kit';

/***** CIAN LIBS *****/
import { ErrorLogComponent } from '@cian/error-log-component';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';

import { initNewbuildingSearch } from '../../utils/initer/initer';

/***** CONTAINERS *****/
import { FiltersContainer, FiltersSectionContainer } from '../FiltersContainer';
import { SeoLinksContainer } from '../SeoLinksContainer';
import { FilterTags } from '../FilterTags';
import { OffersContainer } from '../OffersContainer';
import { SalesDescriptionContainer } from '../SalesDescriptionContainer';
import { SalesLinksContainer } from '../SalesLinksContainer';
import { UpcomingSalePopupContainerWrapper } from '../UpcomingSalePopupContainer';
import { LazyNewbuildingPromoCarouselContainer } from '../NewbuildingPromoCarousel';
import { Heading } from '../Heading';
import { ActionsCentersLoadable } from './internal/components/ActionsCenters/ActionsCentersLoadable';

/***** COMPONENTS *****/
import { PageLayout } from '../../components/PageLayout';

/***** STYLES *****/
import * as styles from './App.css';

/***** TYPES *****/
import { IApplicationState, TReduxStore } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { initJsonQuery } from '../../actions/filters';
import { setTags } from '../../actions/filterTags';
import { setLocationPage } from '../../actions/location';
import { getOffersData } from '../../actions/offersData';
import { setMedia } from '../../actions/media';
import { DEFAULT_RESIZE_TIMEOUT } from '../../constants/timeouts';
import { setCurrentRegion } from '../../actions/region/currentRegion';
import { AppLayout } from '../../components/AppLayout';
import { triggerHeaderRegionChanged } from '../../utils/events/triggerHeaderRegionChanged';
import { trackingActionShowSimilarJK } from '../../actions/analytics';
import { displayConsultantPhone } from '../../utils/displayNewbuildingConsultantPhone';
import { ICustomContextParams } from '../../types/ICustomContextParams';
import { IApplicationContext } from '../../types/applicationContext';
import { ApplicationContext } from '../../utils/applicationContext';
import { getFirstSalesStartNewbuildingId, isSalesStartOnboardingShown } from '../../utils/salesStartOnboarding';
import { setSalesStartNewbuildingId, setSalesStartOnboardingStatus } from '../../actions/salesStartOnboarding';
import { SeoMarkedListContainer, SeoMarkedListPlaceholderContainer } from '../SeoMarkedList';
import { SeoCrossLinksContainer } from '../SeoCrossLinksContainer';
import { IVisitedOffersProviderProps, VisitedOffersProvider } from '@cian/frontend-visited-offers-widget';
import { ContentLayout } from '../../components/ContentLayout';
import { BuilderChatPopupContainer } from '../BuilderChatPopup/BuilderChatPopupContainer';
import { ConsultantCianChat } from '../ConsultantCianChat';
import { OffersFooterContainer } from '../OffersContainer/OffersFooterContainer';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
  customContext: ICustomContextParams;
}

/***** COMPONENT *****/
export class Application extends React.Component<IApplicationProps> {
  private handlePopState: ReturnType<typeof this.getPopStateHandler>;

  public componentDidMount() {
    initNewbuildingSearch({
      logger: this.props.logger,
      regionId: this.props.reduxStore.getState().region.currentRegion.id,
      store: this.props.reduxStore,
      subdomain: this.getRealSubdomain(),
      httpApi: this.props.httpApi,
    });
    const dispatch = this.props.reduxStore.dispatch as TTypedThunkDispatch;
    const storeState = this.props.reduxStore.getState();
    const similarNewbuildings =
      storeState.offersData.similarNewbuildings && storeState.offersData.similarNewbuildings.length;
    const consultantPhone = this.props.config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions =
      this.props.config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];

    displayConsultantPhone(storeState, consultantPhoneEnabledRegions, consultantPhone);
    this.initPushState(storeState, dispatch);

    if (similarNewbuildings) {
      dispatch(trackingActionShowSimilarJK(storeState.jsonQuery));
    }

    this.handlePopState = this.getPopStateHandler(dispatch);
    window.addEventListener('popstate', this.handlePopState, false);
    this.handleResize();
    window.addEventListener('resize', this.handleResize, false);

    if (!isSalesStartOnboardingShown()) {
      const firstSalesStartNewbuildingId = getFirstSalesStartNewbuildingId(
        storeState.offersData.newbuildings,
        storeState.offersData.newbuildingPromoSnippet?.newbuilding,
      );

      if (firstSalesStartNewbuildingId) {
        dispatch(setSalesStartNewbuildingId(firstSalesStartNewbuildingId));
        dispatch(setSalesStartOnboardingStatus(true));
      }
    }
  }

  public componentWillUnmount(): void {
    window.removeEventListener('popstate', this.handlePopState, false);
    window.removeEventListener('resize', this.handleResize, false);
  }

  public render() {
    const { logger, httpApi, config, customContext, microfrontendsRegistry, eventQueue } = this.props;

    const { settings, enabled } = config.get<IVisitedOffersProviderProps>('audience.visitedOffers') || {};

    return (
      <ErrorLogComponent logger={logger}>
        <ApplicationContext.Provider value={{ config, httpApi, logger, microfrontendsRegistry, eventQueue }}>
          <UiKitRoot deviceType={uaToDeviceType(customContext.userAgent)}>
            <UiKitToastsContextProvider>
              <Provider store={this.props.reduxStore}>
                <ActionsCentersLoadable />
                <MicrofrontendProvider registry={microfrontendsRegistry}>
                  <VisitedOffersProvider settings={settings} enabled={enabled}>
                    <AppLayout>
                      <SeoLinksContainer />
                      <div>
                        <div className={styles[`fake_header`]} />
                        <FiltersSectionContainer>
                          <FiltersContainer />
                          <FilterTags />
                        </FiltersSectionContainer>
                        <PageLayout>
                          <ContentLayout>
                            <Heading />
                          </ContentLayout>
                          <SeoMarkedListContainer />
                          <ContentLayout>
                            <SalesLinksContainer />
                            <SalesDescriptionContainer />
                            <OffersContainer />
                          </ContentLayout>

                          <ErrorLogComponent logger={logger}>
                            <LazyNewbuildingPromoCarouselContainer />
                          </ErrorLogComponent>
                          <OffersFooterContainer />
                          <SeoCrossLinksContainer />
                          <SeoMarkedListPlaceholderContainer />
                        </PageLayout>
                      </div>
                      <BuilderChatPopupContainer />
                      <ConsultantCianChat />
                      <UpcomingSalePopupContainerWrapper />
                    </AppLayout>
                  </VisitedOffersProvider>
                </MicrofrontendProvider>
              </Provider>
            </UiKitToastsContextProvider>
          </UiKitRoot>
        </ApplicationContext.Provider>
      </ErrorLogComponent>
    );
  }

  private getRealSubdomain = () => {
    return window.location.host.split('.')[0];
  };

  private initPushState = (state: IApplicationState, dispatch: TTypedThunkDispatch) => {
    const {
      jsonQuery,
      region: { currentRegion },
      location: { path, page, subdomain },
      offersData: { seo },
      filters: { tags },
    } = state;
    const title = (seo && seo.title) || document.title || '';

    triggerHeaderRegionChanged(currentRegion);

    const isGeoPathAvaliable = this.isGeoPathAvaliable(subdomain);
    const pathPrefix = isGeoPathAvaliable ? `/${subdomain}` : '';
    const fullPath = `${pathPrefix}${path}`;

    if (isGeoPathAvaliable) {
      dispatch(
        setLocationPage({
          geoPath: subdomain,
          path: fullPath,
        }),
      );
    }

    window.history.replaceState(
      {
        jsonQuery,
        currentRegion,
        path: fullPath,
        page,
        title,
        tags,
      },
      title,
      fullPath,
    );
  };

  private isGeoPathAvaliable = (subdomain: string) => {
    const realSubdomain = this.getRealSubdomain();

    if (realSubdomain !== 'www') {
      return false;
    }

    return realSubdomain !== subdomain;
  };

  private handleResize = debounce(DEFAULT_RESIZE_TIMEOUT, () => {
    const dispatch = this.props.reduxStore.dispatch;

    dispatch(setMedia());
  });

  private getPopStateHandler =
    (dispatch: TTypedThunkDispatch) =>
    ({ state }: PopStateEvent) => {
      if (state && state.jsonQuery && state.path && state.page) {
        document.title = state.title || document.title;
        dispatch(initJsonQuery(state.jsonQuery));
        dispatch(setCurrentRegion(state.currentRegion));
        dispatch(setTags(state.tags || []));
        dispatch(
          getOffersData({
            reason: 'popstate',
            shouldUpdateHistory: false,
            path: state.path,
            page: state.page,
          }),
        );
      }
    };
}
